import { useContext, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Attachments from 'src/components/post/Attachments';
import ToastContext from 'src/contexts/toast-context';
import { useAPI } from 'src/services/api';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const PostFormModal = ({ show, onHide, setPosts, isEditing, postToEdit, onExited }) => {
  const [title, setTitle] = useState('');
  const [captions, setCaptions] = useState([]);

  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [link, setLink] = useState('');
  const { success, handleError, error } = useContext(ToastContext);
  const api = useAPI();

  useEffect(() => {
    if (isEditing && postToEdit) {
      setTitle(postToEdit?.title ?? '');
      setContent(postToEdit?.content ?? '');
      setAttachments(postToEdit?.attachments ?? []);
      setLink(postToEdit?.videoLink?.attachmentUrl ?? '');
      setCaptions(postToEdit?.attachments?.map((attachment) => attachment.caption) ?? []);
    } else {
      clearForm();
    }
  }, [isEditing, postToEdit]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handlePost();
  };

  const onPost = async () => {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('videoLink', link);

      attachments.forEach((attachment, index) => {
        formData.append('captions', captions[index] ?? '');
        formData.append('attachments', attachment);
      });

      const { data } = await api.post('/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPosts((posts) => [{ ...data }, ...posts]);
      setAttachments((attachments) => [attachments, ...attachments]);
      success('Created new post!');
      setCaptions([]);
      clearForm();
      onHide();
    } catch (e) {
      handleError(e);
    }
  };

  const onUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('videoLink', link);
      attachments.forEach((attachment, index) => {
        if (attachment instanceof File) {
          formData.append('newAttachments', attachment);
          formData.append('newAttachmentsCaptions', captions[index]);
        } else {
          attachment.caption = captions[index];
          formData.append('attachments', JSON.stringify(attachment));
        }
      });

      let { data } = await api.patch(`/posts/${postToEdit.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPosts((posts) => posts.map((p) => (p.id === postToEdit.id ? { ...p, ...data } : p)));
      success('Updated post!');
      setCaptions([]);
      clearForm();
      onHide();
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  const clearForm = () => {
    setTitle('');
    setContent('');
    setAttachments([]);
    setLink('');
  };

  const handlePost = () => {
    if (title.trim() === '' && content.trim() === '') {
      error('Post cannot have a blank title and description');
      return;
    }
    if (isEditing) {
      onUpdate();
    } else {
      onPost();
    }
  };

  return (
    <Dialog open={show} onClose={onHide} id="post-form-modal" maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Post' : 'Create Post'}</DialogTitle>
      <DialogContent>
        <Form onSubmit={handleFormSubmit}>
          <div className="flex gap-2 items-center">
            <label htmlFor="formTitle">Title</label>
            <input
              type="text"
              className="block grow rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              id="formTitle"
              placeholder="Enter title"
              value={title}
              onChange={handleTitleChange}
              maxLength={500}
            />
          </div>

          <Form.Group controlId="formContent" className="mt-3" data-cy="formContent">
            <Form.Label>Description</Form.Label>
            <ReactQuill
              value={content}
              onChange={setContent}
              placeholder="Enter description"
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link'],
                ],
              }}
            />
          </Form.Group>
          <Form.Group controlId="formAttachments" className="mt-3">
            <Form.Label>Attachments</Form.Label>
            <Attachments
              attachments={attachments}
              setAttachments={setAttachments}
              link={link}
              onLinkSave={(link) => setLink(link)}
              onLinkDelete={() => setLink('')}
              captions={captions}
              setCaptions={setCaptions}
              isEditing={isEditing}
            />
          </Form.Group>
        </Form>
      </DialogContent>
      <DialogActions className="gap-2">
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          id="post-submit"
          onClick={handleFormSubmit}
          data-testid="submit-button"
        >
          {isEditing ? 'Update' : 'Post'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostFormModal;
